<template>
  <div class="index container">
    <transition name="fade">
      <router-view @showParent="showParent"> </router-view>
    </transition>

    <div v-if="showParentPage">
      <v-card elevation="0" outlined class="mt-5">
        <v-sheet class="pa-4 d-md-flex">
          <v-sheet class="flex-sm-column">
            <p class="text-h4 ma-0">Checklist</p>
          </v-sheet>
          <v-sheet class="flex-sm-column my-3 my-md-0">
            <v-breadcrumbs class="pa-md-3 pa-0">
              <v-breadcrumbs-item
                :exact="true"
                :to="{ name: 'home', params: {} }"
                >Home</v-breadcrumbs-item
              >
              <v-breadcrumbs-divider>/</v-breadcrumbs-divider>
              <v-breadcrumbs-item
                :exact="true"
                :to="{ name: 'checklist', params: {} }"
                active
              >
                Checklist
              </v-breadcrumbs-item>
            </v-breadcrumbs>
          </v-sheet>

          <v-spacer></v-spacer>

          <v-sheet class="flex-sm-column">
            <v-row>
              <v-col cols="12" md="5">
                <v-btn
                  class="align-self-end align-self-lg-start text-capitalize"
                  color="primary"
                  block
                  text
                  outlined
                  @click="checklistDialog = true"
                >
                  <v-icon>mdi-plus</v-icon>
                  Add Checklist
                </v-btn>
              </v-col>
            </v-row>
          </v-sheet>
        </v-sheet>
      </v-card>

      <v-card elevation="0" class="mt-5">
        <div class="table-container">
          <v-data-table
            :headers="headers"
            :items="checklist"
            :items-per-page="pagination.pageSize"
            :loading="loading"
            :server-items-length="pagination.totalSize"
            :page="pagination.current"
            :disable-pagination="!checklist || checklist.length < 1"
            :hide-default-header="!checklist || checklist.length < 1"
            :hide-default-footer="!checklist || checklist.length < 1"
            :options.sync="options"
            item-key="id"
            class="elevation-1"
            @update:options="pageChanged"
          >
            <template v-slot:item.checklistName="{ item }">
              {{ item.checklistName }}
              <div class="font-weight-light text-caption">
                {{ item.checklistDescription }}
              </div>
            </template>
            <template v-slot:item.mandatory="{ item }">
              <div v-if="item.mandatory">Mandatory</div>
              <div v-if="item.allowGlobalCheck">Global Checkbox</div>
              <div v-if="item.allowAttachment">Allow Attachment</div>
              <div v-if="item.showOnDashboard">Show on Dashboard</div>
            </template>
            <template v-slot:item.checklistType="{ item }">
              {{ item.type }}
            </template>
            <template v-slot:item.validFrom="{ item }">
              <span v-if="item.validFrom">
                {{ item.validFrom }} - {{ item.validTo  }}
              </span>
              <span v-else>
                N/A
              </span>
            </template>
            <template v-slot:item.items="{ item }">
              <ul class="pl-4">
                <li v-for="(checklistItem, index) in item.items" :key="index">
                  {{ checklistItem.itemName }}
                </li>
              </ul>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon class="mr-2" @click="edit(item)"> mdi-pencil </v-icon>
              <v-icon color="red" class="mr-2" @click="remove(item)">
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </div>

    <confirmation-dialog ref="confirm" />

    <v-row justify="center">
      <v-dialog v-model="checklistDialog" persistent max-width="600px">
        <v-card>
          <v-card-text>
            <checklist-form
              :key="checklistId"
              :id="checklistId"
              :isModal="true"
              @clicked="closeChecklistDialog"
            ></checklist-form>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import ChecklistForm from "./ChecklistForm.vue";
import ChecklistService from "@/services/checklistService.js";

const ConfirmationDialog = () =>
  import("@/components/common/ConfirmationDialog.vue");

export default {
  components: { ChecklistForm, ConfirmationDialog },
  data() {
    return {
      showParentPage: true,
      searchTerms: "",
      checklistDialog: false,
      checklistId: null,
      loading: false,
      pagination: {
        current: 1,
        pageSize: 10,
        totalSize: 100,
      },
      options: {
        sortBy: [],
        sortDesc: [],
      },
      checklist: [],
      headers: [
        {
          text: "Name",
          value: "checklistName",
          align: "start",
          sortable: true,
        },
        {
          text: "Type",
          value: "checklistType",
          align: "start",
          sortable: true,
        },
        {
          text: "Options",
          value: "mandatory",
          align: "start",
          sortable: true,
        },
        {
          text: "Valid Perioid",
          value: "validFrom",
          align: "start",
          sortable: true,
        },
        {
          text: "Items",
          value: "items",
          align: "start",
          sortable: false,
        },
        { text: "Actions", value: "actions", align: "end", sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters("navigation", ["globalCenter"]),
  },
  methods: {
    ...mapActions("messages", [
      "addMessage",
      "addErrorMessage",
      "addSuccessMessage",
    ]),
    showParent(show) {
      this.showParentPage = show;
    },
    getChecklists() {
      const centerId = this.globalCenter?.id ?? null;

      //default to sorting by ID desc if not defined
      const sortBy =
        this.options["sortBy"] && this.options["sortBy"].length > 0
          ? this.options.sortBy[0]
          : "id";
      const sortDesc =
        this.options["sortDesc"] && this.options["sortDesc"].length > 0
          ? this.options.sortDesc[0]
          : true;

      ChecklistService.getChecklists(
        this.pagination.pageIndex && this.pagination.pageIndex > 0
          ? this.pagination.pageIndex - 1
          : 0,
        this.pagination.pageSize,
        centerId,
        `${sortBy}${sortDesc ? ",desc" : ""}`,
      )
        .then(resp => {
          this.pagination.totalSize = resp.data.totalElements;
          this.checklist = resp.data.content;
        })
        .catch(error => {
          console.log(error);
          const errorMessage =
            error.data?.message || "An unexpected error occurred";
          this.addErrorMessage(errorMessage);
        });
    },
    search() {
      this.getChecklists();
    },
    clearSearch() {
      this.searchTerms = null;
      this.getChecklists();
    },
    pageChanged(pagination) {
      this.pagination.pageIndex = pagination.page;
      if (pagination.itemsPerPage === -1) {
        this.pagination.pageSize = 99999;
      } else {
        this.pagination.pageSize = pagination.itemsPerPage;
      }
      this.getChecklists();
    },
    closeChecklistDialog() {
      this.checklistDialog = false;
      this.checklistId = null;
      this.getChecklists();
    },
    edit(item) {
      this.checklistId = item.id;
      this.checklistDialog = true;
    },
    async remove(item) {
      try {
        const confirm = await this.$refs.confirm.open(
          "Confirm Delete",
          "Are you sure you want to delete this checklist?",
        );
        if (confirm) {
          await ChecklistService.deleteChecklist(item.id);
          this.addSuccessMessage("Checklist successfully deleted.");
          this.getChecklists(); // Refresh the checklist after deletion
        }
      } catch (error) {
        console.error("Error deleting checklist:", error);
        const errorMessage =
          error.response?.data?.message ||
          "An unexpected error occurred while deleting the checklist.";
        this.addErrorMessage(errorMessage);
      }
    },
  },
};
</script>
